// Slice 40 — login. Surfaces backend errors verbatim (the spec §1 envelope
// already gives human-readable copy). 429 throttle → the message names the
// retry window so operators don't refresh-spam.
//
// Slice 49 (V2.2): added a passkey-login button alongside the password
// form. The browser API surfaces (PublicKeyCredential.isUserVerifying-
// PlatformAuthenticatorAvailable + WebAuthnNavigator existence checks)
// gate visibility — if the browser/device has no passkey capability the
// button is hidden rather than showing a guaranteed-to-fail control.

import { startAuthentication } from "@simplewebauthn/browser";
import { useEffect, useState } from "react";
import { Api, RecoveryApi } from "../api/client";
import { HqButton, HqCard, HqInput, Icons } from "../ui";
import { AuthShell } from "./SetupScreen";

export interface LoginScreenProps {
  onLoggedIn: (username: string) => void;
}

type ViewMode = "login" | "recovery";

export function LoginScreen(props: LoginScreenProps) {
  const [mode, setMode] = useState<ViewMode>("login");
  if (mode === "recovery") {
    return (
      <RecoveryView onCancel={() => setMode("login")} onRecovered={(u) => props.onLoggedIn(u)} />
    );
  }
  return <LoginView {...props} onForgotPasskey={() => setMode("recovery")} />;
}

function LoginView({
  onLoggedIn,
  onForgotPasskey,
}: LoginScreenProps & { onForgotPasskey: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);

  // Browser-capability probe. Done once on mount so the buttons are stable.
  useEffect(() => {
    if (typeof window === "undefined" || !("PublicKeyCredential" in window)) return;
    setPasskeyAvailable(true);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const res = await Api.login(username.trim().toLowerCase(), password);
    setBusy(false);
    if (!res.ok) {
      if (res.error.status === 429) {
        setErr("Too many attempts. Wait a minute, then try again.");
      } else if (res.error.code === "passkey_required") {
        setErr(res.error.message);
      } else {
        setErr(res.error.message || "Sign-in failed.");
      }
      return;
    }
    onLoggedIn(res.data.username);
  }

  async function signInWithPasskey() {
    if (!username.trim()) {
      setErr("Enter your username first.");
      return;
    }
    setErr(null);
    setPasskeyBusy(true);
    const begin = await Api.beginPasskeyLogin(username.trim().toLowerCase());
    if (!begin.ok) {
      setPasskeyBusy(false);
      if (begin.error.status === 429) {
        setErr("Too many attempts. Wait a minute, then try again.");
      } else {
        setErr(begin.error.message);
      }
      return;
    }
    let assertion: unknown;
    try {
      assertion = await startAuthentication({
        optionsJSON: begin.data as Parameters<typeof startAuthentication>[0]["optionsJSON"],
      });
    } catch (e) {
      setPasskeyBusy(false);
      setErr(e instanceof Error ? e.message : "Passkey sign-in was cancelled.");
      return;
    }
    const finish = await Api.finishPasskeyLogin(assertion);
    setPasskeyBusy(false);
    if (!finish.ok) {
      setErr(finish.error.message || "Passkey verification failed.");
      return;
    }
    onLoggedIn(finish.data.username);
  }

  return (
    <AuthShell>
      <HqCard
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Icons.Lock width={14} height={14} sw={1.7} style={{ color: "var(--hq-accent)" }} />
            Sign in
          </span>
        }
      >
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Username">
            <HqInput
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </Field>
          <Field label="Password">
            <HqInput
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
          {err && (
            <div
              style={{
                padding: "8px 12px",
                borderRadius: "var(--hq-radius-md)",
                background: "var(--hq-danger-soft)",
                color: "var(--hq-danger)",
                fontSize: 12,
                border: "1px solid rgba(251,113,133,.25)",
              }}
            >
              {err}
            </div>
          )}
          <HqButton
            type="submit"
            variant="primary"
            size="lg"
            loading={busy}
            style={{ marginTop: 4 }}
          >
            Sign in with password
          </HqButton>
          {passkeyAvailable && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  margin: "4px 0",
                  color: "var(--hq-text-dim)",
                  fontSize: 10.5,
                  letterSpacing: ".15em",
                  textTransform: "uppercase",
                  fontFamily: "var(--hq-font-mono)",
                }}
              >
                <div style={{ flex: 1, height: 1, background: "var(--hq-border)" }} />
                <span>or</span>
                <div style={{ flex: 1, height: 1, background: "var(--hq-border)" }} />
              </div>
              <HqButton
                type="button"
                variant="secondary"
                size="lg"
                loading={passkeyBusy}
                onClick={() => void signInWithPasskey()}
              >
                <Icons.Shield width={14} height={14} sw={1.7} />
                Sign in with passkey
              </HqButton>
            </>
          )}
          <p
            style={{
              margin: "8px 0 0",
              fontSize: 11,
              color: "var(--hq-text-dim)",
              textAlign: "center",
            }}
          >
            Session cookie · HttpOnly · idle 12 h / absolute 7 d.
          </p>
          <button
            type="button"
            onClick={onForgotPasskey}
            style={{
              margin: "4px auto 0",
              background: "transparent",
              border: "none",
              color: "var(--hq-text-dim)",
              fontSize: 11.5,
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Lost your passkey? Use a recovery code
          </button>
        </form>
      </HqCard>
    </AuthShell>
  );
}

function RecoveryView({
  onCancel,
  onRecovered,
}: {
  onCancel: () => void;
  onRecovered: (username: string) => void;
}) {
  type Step =
    | { kind: "code" }
    | { kind: "password"; token: string; expiresAt: number; usernameUsed: string };
  const [step, setStep] = useState<Step>({ kind: "code" });
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const res = await RecoveryApi.begin(username.trim().toLowerCase(), code.trim());
    setBusy(false);
    if (!res.ok) {
      if (res.error.status === 429) {
        setErr("Too many attempts. Wait a minute, then try again.");
      } else {
        setErr(res.error.message || "Invalid code.");
      }
      return;
    }
    setStep({
      kind: "password",
      token: res.data.recoveryToken,
      expiresAt: res.data.expiresAt,
      usernameUsed: username.trim().toLowerCase(),
    });
    setCode("");
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (step.kind !== "password") return;
    setErr(null);
    if (newPassword.length < 12) {
      setErr("Password must be at least 12 characters.");
      return;
    }
    if (newPassword !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    const res = await RecoveryApi.finish(step.token, newPassword);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message || "Recovery failed.");
      return;
    }
    onRecovered(res.data.username);
  }

  return (
    <AuthShell>
      <HqCard
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Icons.Shield width={14} height={14} sw={1.7} style={{ color: "var(--hq-accent)" }} />
            Account recovery
          </span>
        }
      >
        {step.kind === "code" ? (
          <form onSubmit={submitCode} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--hq-text-muted)" }}>
              Enter your username and one of your single-use backup codes. You'll set a new password
              on the next step.
            </p>
            <Field label="Username">
              <HqInput
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Field>
            <Field label="Recovery code">
              <HqInput
                placeholder="XXXXX-XXXXX"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                mono
                required
                autoFocus
              />
            </Field>
            {err && <ErrorBox>{err}</ErrorBox>}
            <HqButton type="submit" variant="primary" size="lg" loading={busy}>
              Verify code
            </HqButton>
            <HqButton type="button" variant="secondary" onClick={onCancel}>
              Back to sign-in
            </HqButton>
          </form>
        ) : (
          <form
            onSubmit={submitPassword}
            style={{ display: "flex", flexDirection: "column", gap: 12 }}
          >
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--hq-text-muted)" }}>
              Set a new password for{" "}
              <strong style={{ color: "var(--hq-text)" }}>{step.usernameUsed}</strong>. You'll be
              signed in once this succeeds.
            </p>
            <Field label="New password (min 12 chars)">
              <HqInput
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
              />
            </Field>
            <Field label="Confirm new password">
              <HqInput
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </Field>
            {err && <ErrorBox>{err}</ErrorBox>}
            <HqButton type="submit" variant="primary" size="lg" loading={busy}>
              Set password & sign in
            </HqButton>
            <HqButton type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </HqButton>
          </form>
        )}
      </HqCard>
    </AuthShell>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "8px 12px",
        borderRadius: "var(--hq-radius-md)",
        background: "var(--hq-danger-soft)",
        color: "var(--hq-danger)",
        fontSize: 12,
        border: "1px solid rgba(251,113,133,.25)",
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)" }}>{label}</span>
      {children}
    </label>
  );
}
